<template>
  <div
      v-if="isVisible"
      class="product-card-calculator"
      ref="root"
  >
    <BaseGridRow small>
      <BaseGridCol v-if="!product.isInternet">
        <BaseCalculatorUsageField
            label="Dein Verbrauch*"
            v-model="form.usage"
            :branch="branch.mainCategory.key || 'power'"
            :is-valid="showValidCheck.usage"
            :valid-color="[branch.customColor.primary, branch.customColor.secondary]"
            no-initial-value
            @update:modelValue="hasFocusedOutOnce.usage = true"
            @keyup="checkUsageValid"
            @focusout="() => { checkUsageValid(); hasFocusedOutOnce.usage = true }"
        />
      </BaseGridCol>
      <BaseGridCol v-if="showPostcode">
        <InputField
            label="Deine Postleitzahl"
            :only-numeric="true"
            error-numeric="Ungültige Postleitzahl"
            :max-length="5"
            :min-length="5"
            :show-loader="isFetching"
            :is-valid="showValidCheck.postcode"
            :valid-color="[branch.customColor.primary, branch.customColor.secondary]"
            v-model="form.postcode"
            @keyup="checkPostcodeValid"
        />
      </BaseGridCol>
      <BaseGridCol v-if="showCity">
        <SelectField
            label="Deine Stadt*"
            v-model="form.city"
            :readonly="selectableCities.length <= 1"
            :options="cityOptions"
            :is-valid="showValidCheck.city"
            :valid-color="[branch.customColor.primary, branch.customColor.secondary]"
            @update:modelValue="hasFocusedOutOnce.city = true"
        />
      </BaseGridCol>
      <BaseGridCol v-if="showStreet">
        <SelectField
            label="Straße*"
            v-model="form.street"
            :options="streetsOptions"
            :search="true"
            :is-valid="showValidCheck.street"
            :valid-color="[branch.customColor.primary, branch.customColor.secondary]"
            @update:modelValue="hasFocusedOutOnce.street = true"
        />
      </BaseGridCol>
      <BaseGridCol v-if="showHouseNumber">
        <InputField
            label="Hausnummer*"
            type="text"
            v-model="form.houseNumber"
            :is-valid="showValidCheck.houseNumber"
            :valid-color="[branch.customColor.primary, branch.customColor.secondary]"
            @keyup="checkHousenumberValid"
            @focusout="hasFocusedOutOnce.houseNumber = true"
        />
      </BaseGridCol>
      <BaseGridCol v-if="product.isInternet && showHouseNumber">
        <Checkbox v-model="form.business">
          Ich bin Geschäftskund:in
        </Checkbox>
      </BaseGridCol>
      <BaseGridCol>
        <BaseButton
            :branch="branch"
            width="full"
            :style="{
              background: `linear-gradient(90deg, ${branch.customColor.primary}, ${branch.customColor.secondary})`,
            }"
            @click.prevent="submit"
        >
          {{ buttonLabel }}
        </BaseButton>
      </BaseGridCol>
    </BaseGridRow>
  </div>
</template>

<script lang="ts" setup>
import Product from '@/classes/Product';
import BaseButton from '@/components/BaseButton.vue';
import BaseGridCol from '@/components/BaseGridCol.vue';
import BaseGridRow from '@/components/BaseGridRow.vue';
import Checkbox from '@/components/Checkbox.vue';
import BranchInterface from '@/interfaces/BranchInterface';
import CalculatorModelInterface from '@/interfaces/CalculatorModelInterface';
import {
  reactive,
  computed,
  watch,
  ref,
  onBeforeUnmount, WatchStopHandle,
} from 'vue';
import { useRouter } from 'vue-router';
import {
  EnetAPI,
} from '@/api';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import BaseCalculatorUsageField from '@/components/BaseCalculator/BaseCalculatorUsageField.vue';
import CityInterface from '@/interfaces/CityInterface';
import StreetInterface from '@/interfaces/StreetInterface';
import { env } from '@/helpers';
import { useStore } from 'vuex';

const props = withDefaults(defineProps<{
  product: Product
  postcode?: string
  buttonLabel?: string
  options?: CalculatorModelInterface
}>(), {
  forceVisibility: false,
  buttonLabel: 'Tarif finden',
  options: () => ({}),
});

const router = useRouter();
const store = useStore();
const root = ref<InstanceType<typeof HTMLElement>>();

const isVisible = computed<boolean>(() => (env('CALCULATOR_ACTIVE') as boolean || [true, 'true', 1].includes((props.forceVisibility || 0))));
const branch = computed<BranchInterface | undefined>(() => store.getters['branches/all'].find((br: BranchInterface) => br.mainCategory.key === props.product.branch));

const valid = reactive({
  postcode: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const hasFocusedOutOnce = reactive({
  postcode: false,
  city: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const form = ref<CalculatorModelInterface>({
  ...{
    productId: props.product.id,
    branch: props.product.branch,
    postcode: undefined,
    city: undefined,
    street: undefined,
    houseNumber: undefined,
    usage: undefined,
    business: false,
  },
  ...props.options,
});

const postcodeLoaded = ref<string | number>('');
const preselectedPostcodeSet = ref(false);
const selectableCities = ref<CityInterface[]>([]);
const selectableStreets = ref<StreetInterface[]>([]);

const showValidCheck = computed(() => ({
  postcode: hasFocusedOutOnce.postcode ? valid.postcode : undefined,
  city: hasFocusedOutOnce.city ? !!form.value.city : undefined,
  street: hasFocusedOutOnce.street ? !!form.value.street : undefined,
  houseNumber: hasFocusedOutOnce.houseNumber ? valid.houseNumber : undefined,
  usage: hasFocusedOutOnce.usage ? valid.usage : undefined,
}));

const isFetching = ref(false);
const fetchCitiesAndStreets = ($postcode: string | number) => {
  if (`${$postcode}`.length === 5 && $postcode !== postcodeLoaded.value) {
    selectableCities.value = [];
    selectableStreets.value = [];
    valid.postcode = false;
    hasFocusedOutOnce.city = false;
    hasFocusedOutOnce.street = false;
    hasFocusedOutOnce.houseNumber = false;
    hasFocusedOutOnce.usage = false;

    postcodeLoaded.value = $postcode as string;

    isFetching.value = true;
    EnetAPI.getCitiesAndStreets($postcode).then((data) => {
      selectableCities.value = data.cities;
      selectableStreets.value = data.streets;
      if (!((props.options.postcode === $postcode) && !preselectedPostcodeSet.value)) {
        form.value.city = selectableCities.value[0].city;
        if (selectableCities.value.length === 1) {
          form.value.city = selectableCities.value[0].city;
          hasFocusedOutOnce.city = true;
        } else {
          form.value.city = undefined;
        }
        if (selectableStreets.value.length === 1) {
          hasFocusedOutOnce.street = true;
          form.value.street = selectableStreets.value[0].street;
        } else {
          form.value.street = undefined;
        }
        preselectedPostcodeSet.value = true;
      }
      valid.postcode = true;
    }).catch(() => {
      valid.postcode = false;
    }).finally(() => {
      isFetching.value = false;
      hasFocusedOutOnce.postcode = true;
    });
  }
};

watch(() => form.value.postcode, ($newPostcode) => {
  fetchCitiesAndStreets($newPostcode as string);
});

if (form.value.postcode && `${form.value.postcode}`.length === 5) {
  fetchCitiesAndStreets(form.value.postcode);
}

const cityOptions = computed(() => [...selectableCities.value].map(($s) => ({
  label: $s.city,
  value: $s.city,
})));

const streetsOptions = computed(() => [...selectableStreets.value].map(($s) => ({
  label: $s.street,
  value: $s.street,
})));

const showCity = computed<boolean>(() => (
  `${form.value.postcode || ''}`.trim().length === 5
    && cityOptions.value.length > 0
));
const showStreet = computed<boolean>(() => (
  showCity.value
    && streetsOptions.value.length > 0
));
const showHouseNumber = computed<boolean>(() => showStreet.value);
const showPostcode = computed<boolean>(() => props.product.isInternet || !!form.value.usage);

const submit = () => {
  let path = '/preisanzeige/';

  if (!form.value.postcode) return;
  if (`${form.value.postcode || ''}`.trim().length !== 5) return;
  if (!form.value.city) return;
  if (!form.value.street) return;
  if (!form.value.houseNumber) return;

  const params: Record<string, string> = {
    stadt: form.value.city as string,
    strasse: form.value.street as string,
    hausnummer: form.value.houseNumber as string,
  };

  if (form.value.business) {
    params.gewerbe = '1';
  }

  if (form.value.productId) {
    params.pid = `${form.value.productId}`;
  }

  const urlParams = new URLSearchParams(params);

  path += `${branch.value?.slug}/${form.value.postcode}/${form.value.usage}/?${urlParams.toString()}`;

  router.push(path);
};

const checkPostcodeValid = () => {
  valid.postcode = `${form.value.postcode || ''}`.trim().length === 5 && selectableCities.value.length > 0;
};

const checkUsageValid = () => {
  valid.usage = parseInt(`${form.value.usage || ''}`, 10) > 0;
};

watch(() => form.value.usage, checkUsageValid);

const checkHousenumberValid = () => {
  valid.houseNumber = `${form.value.houseNumber || ''}`.trim().length >= 1;
};

let callbackWatcher: WatchStopHandle;

onBeforeUnmount(() => {
  if (callbackWatcher) {
    callbackWatcher();
  }
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.product-card-calculator {
  width: 100%;
  max-width: 100%;
  text-align: left;

  .calculator__branches {
    display: flex;
    gap: 30px;

    > div {
      flex: 0 1 auto;
    }
  }
}
</style>
